import { useEffect, useState } from 'react';
import { Navigate, Outlet, matchPath, useLocation, useParams } from 'react-router-dom';
import Layout from '~/components/shared/layout/Layout';
import MainTitle from '~/components/shared/layout/MainTitle';
import Loader from '~/components/shared/loader/Loader';
import { SupportFooter } from '~/components/shared/support-footer/SupportFooter';
import { ExpandableBasket } from '../campaign/components/expandable-basket/ExpandableBasketTestVersion';
import { StepListContent } from '../campaign/components/steps/StepList';
import { useBasket } from './useBasket';

import { useTitleCallback } from '~/hooks/useTitle';
import { FunnelAnalytics } from './FunnelAnalytics';
import { StepNavigationV2 } from './steps/StepNavigationV2';
import { useMappedCampaign } from './v2.utils';
import { v2Routes, v2Steps } from './v2routes';

export const CampaignV2 = () => {
  const setTitle = useTitleCallback();
  const { pathname } = useLocation();
  // we know that "campaignUrl" is defined here
  const { campaignUrl = '' } = useParams<{ campaignUrl: string }>();

  const dwellingUnit = useBasket((s) => s.dwellingUnit);
  const partnerAlias = useBasket((s) => s.partnerAlias);
  const selectedProducts = useBasket((s) => s.selectedProducts);
  const isSummaryOpen = useBasket((s) => s.isSummaryOpen);
  const toggleSummary = useBasket((s) => s.toggleSummary);
  const resetBasketForCampaign = useBasket((s) => s.resetBasketForCampaign);
  // basket state is reset on campaign change
  const { data, error } = useMappedCampaign(partnerAlias, dwellingUnit, campaignUrl);
  const [isBasketReset, setIsBasketReset] = useState(false);

  useEffect(() => {
    if (data) {
      resetBasketForCampaign(data);
      // must wait for reset to render since we otherwise clear selected bundleId etc after render
      setIsBasketReset(true);
    }
  }, [data, resetBasketForCampaign]);

  if (error || !campaignUrl) {
    return <Navigate to={v2Routes.index} />;
  }
  if (!data || !isBasketReset) {
    return <Loader />;
  }

  // Figure out current step
  const stepRoute = v2Steps.find((route) => matchPath(`${v2Routes.absoluteStepPathBase}/${route.stepPath}`, pathname));
  if (!stepRoute) {
    console.error('Step route not found', pathname, v2Steps);
    return <Navigate to={v2Routes.index} />;
  }
  const currentStep = v2Steps.indexOf(stepRoute);
  const currentStepPlusOne = currentStep + 1;
  const mainSubscriptionName = data.products.subscription[0].offers[0].displayData.name;
  setTitle(`${mainSubscriptionName} - Steg ${currentStepPlusOne}: ${stepRoute.title} `, true);

  return (
    <div>
      <FunnelAnalytics campaign={data} currentStep={currentStep} />
      {/* STEPS */}
      <div className="campaign light">
        <MainTitle isCampaignTitle={true}>Bestilling av {mainSubscriptionName}</MainTitle>
        <div className="steps" aria-hidden={isSummaryOpen}>
          <nav className="steps-header" aria-label="Stegindikator bestillingsløp" tabIndex={-1}>
            <StepListContent steps={v2Steps} currentStep={currentStep} />
          </nav>
          <Layout
            focusOnRender={true}
            wide={stepRoute.wideLayout}
            aria-label={`Bestilling av ${mainSubscriptionName}, Steg ${currentStepPlusOne} av ${v2Steps.length} - ${stepRoute.title}`}
          >
            {/* render current step / child route */}
            <Outlet context={data} />
          </Layout>
        </div>
        {stepRoute?.stepPath !== v2Routes.campaignSummary && (
          <ExpandableBasket selectedProducts={selectedProducts} hideShipping={false} isOpen={isSummaryOpen} toggleSummary={toggleSummary} />
        )}
        <StepNavigationV2 isSummaryOpen={isSummaryOpen} currentStep={currentStep} />
      </div>
      <SupportFooter accommodateShoppingCart />
    </div>
  );
};
